import { colors, createMediaQuery } from 'styles'
import { lighten, darken, em } from 'polished'
import { VariantSlug } from 'data/product'

interface Breakpoints extends Array<string> {
  med: string
  lg: string
}

export interface Theme {
  name: ThemeName
  breakpoints: Breakpoints
  space: readonly number[]
  colors: ThemeColors & {
    text: string
    gray1: string
    white: string
    doveGray: string
    whisper: string
    gainsboro: string
    matterhorn: string
    steelGray: string
    black: string
    silverMist: string
    snow: string
  }
  bgOpacity: number
  fonts: {
    regular: string
    screen: string
    bold: string
  }
  buttons: {
    primary: any
    nuPrimary: any
    nuTertiary: any
    modal: any
    cta: any
    pill: any
    empty: any
  }
}

export const DESKTOP_MIN_WIDTH = 769

export const MEDIUM_MIN_WIDTH = 376

const breakpoints: any = [
  em(`${MEDIUM_MIN_WIDTH}px`),
  em(`${DESKTOP_MIN_WIDTH}px`),
]

breakpoints.med = breakpoints[0]
breakpoints.lg = breakpoints[1]

export const baseTheme = {
  breakpoints,
  space: [0, 8, 16, 24, 32, 48, 64, 128],
  colors,
  // Each family carries the metrics-matched fallback (see styles/font) then a
  // grotesque system fallback, so bold text and headings degrade to Helvetica/
  // Arial — not the browser's default serif — during the font-display: swap
  // window or if a face fails to load.
  fonts: {
    regular:
      "'PxGroteskRegular', 'PxGrotesk Fallback', 'Helvetica Neue', Arial, sans-serif",
    screen:
      "'PxGroteskScreen', 'PxGrotesk Screen Fallback', 'Helvetica Neue', Arial, sans-serif",
    bold: "'PxGroteskBold', 'PxGrotesk Bold Fallback', 'Helvetica Neue', Arial, sans-serif",
    boldItalic:
      "'PxGroteskBoldItalic', 'PxGrotesk Bold Fallback', 'Helvetica Neue', Arial, sans-serif",
  },
} as const

interface ThemeColors {
  primary: string
  backgroundPrimary: string
  backgroundSecondary: string
  progressIndicator: string
  activeText: string
  text: string
  swatchPalette: SwatchPalette
  titleTextShadow: string
  artRestingShadow: string
  artFeatureShadow: string
  artOverlay: string
  loadingShadow: string
  journeyTextBackground: string
  journeyTextUnderline: string
  journeyEndVariantBackground: string
}

const createTheme = (
  name: VariantSlug | 'default',
  bgOpacity: number,
  themeColors: ThemeColors
): Theme | DefaultTheme => {
  const textColor = themeColors.text
  return {
    ...baseTheme,
    name,
    bgOpacity,
    colors: {
      ...baseTheme.colors,
      ...themeColors,
    },
    fonts: {
      ...baseTheme.fonts,
    },
    buttons: {
      primary: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: textColor,
        '&:hover, &:active': {
          color: themeColors.activeText,
        },
      },
      nuPrimary: {
        border: `1px solid ${colors.black}`,
        backgroundColor: colors.black,
        color: colors.white,
        fontFamily: baseTheme.fonts.bold,
        textTransform: 'uppercase',
        transition: 'box-shadow 0.5s',
        fontSize: em('11px'),
        letterSpacing: '1.2px',
        lineHeight: '19px',
        '&:hover': {
          backgroundColor: colors.white,
          color: colors.black,
          boxShadow: `2px 2px 0px ${colors.black}`,
        },
        '&:active': {
          backgroundColor: colors.athensGray,
          color: colors.black,
          boxShadow: `inset 2px 2px 0px ${colors.black}`,
        },
        [createMediaQuery(baseTheme.breakpoints.med)]: {
          fontSize: em('12px'),
          letterSpacing: '1.4px',
          lineHeight: '20px',
        },
        [createMediaQuery(baseTheme.breakpoints.lg)]: {
          fontSize: em('16px'),
          letterSpacing: '2px',
          lineHeight: '26px',
        },
      },
      nuTertiary: {
        backgroundColor: colors.white,
        border: `2px solid ${colors.gainsboro}`,
        color: colors.black,
        fontFamily: baseTheme.fonts.bold,
        textTransform: 'uppercase',
        transition: 'box-shadow 0.5s',
        fontSize: em('11px'),
        letterSpacing: '1.2px',
        lineHeight: '19px',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: `2px 2px 0px ${colors.black}`,
        },
        '&:active': {
          boxShadow: `inset 2px 2px 0px ${colors.black}`,
        },
        [createMediaQuery(baseTheme.breakpoints.med)]: {
          fontSize: em('12px'),
          letterSpacing: '1.4px',
          lineHeight: '20px',
        },
        [createMediaQuery(baseTheme.breakpoints.lg)]: {
          fontSize: em('16px'),
          letterSpacing: '2px',
          lineHeight: '26px',
        },
      },
      pill: {
        alignItems: 'center',
        border: `2px solid ${themeColors.text}`,
        borderRadius: '16px',
        color: textColor,
        display: 'flex',
        fontFamily: baseTheme.fonts.regular,
        fontSize: em('12px'),
        justifyContent: 'center',
        letterSpacing: '0.5px',
        lineHeight: '14px',
        padding: '1px 8px 3px',
        textAlign: 'center',
        textDecoration: 'none',
        textTransform: 'uppercase',
        '&:hover, &:active': {
          border: `2px solid ${themeColors.activeText}`,
          color: themeColors.activeText,
        },
        [createMediaQuery(baseTheme.breakpoints.lg)]: {
          padding: '1px 14px 3px',
          fontSize: em('16px'),
          letterSpacing: '2.6px',
          lineHeight: '26px',
        },
      },
      modal: {
        color: colors.white,
        backgroundColor: colors.black,
        textTransform: 'uppercase',
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: em('18px'),
        letterSpacing: '0.5px',
        lineHeight: 1,
        height: '48px',
        [createMediaQuery(baseTheme.breakpoints.med)]: {
          fontSize: em('20px'),
          letterSpacing: '1.1px',
          height: '56px',
        },
        [createMediaQuery(baseTheme.breakpoints.lg)]: {
          fontSize: em('24px'),
          letterSpacing: '1.6px',
          height: '64px',
        },
        '&:hover, &:active': {
          color: colors.white,
          backgroundColor: lighten(0.3, colors.black),
        },
      },
      cta: {
        color: colors.white,
        backgroundColor: colors.black,
        textTransform: 'uppercase',
        textDecoration: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        fontSize: em('16px'),
        letterSpacing: '0.44px',
        lineHeight: 1,
        height: '40px',
        border: `3px solid ${colors.white}`,
        [createMediaQuery(baseTheme.breakpoints.med)]: {
          fontSize: em('20px'),
          letterSpacing: '1.1px',
          height: '48px',
        },
        [createMediaQuery(baseTheme.breakpoints.lg)]: {
          fontSize: em('24px'),
          letterSpacing: '1.6px',
          height: '64px',
        },
        '&:hover, &:active': {
          color: colors.white,
          backgroundColor: lighten(0.3, colors.black),
        },
        '& svg': {
          fill: colors.white,
        },
      },
      empty: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 'none',
        padding: 0,
        background: 'none',
        border: '1px dashed',
        borderColor: 'transparent',
        color: textColor,
        '&:focus': {
          borderColor: themeColors.activeText,
        },
        [createMediaQuery(baseTheme.breakpoints.lg)]: {
          '&:hover': {
            color: themeColors.activeText,
          },
        },
      },
    },
  }
}

interface DefaultTheme extends Theme {
  name: 'default'
}

export type ThemeName = 'default' | VariantSlug

export interface SwatchPalette {
  primary: string
  secondary?: string
}

const themes = [
  createTheme('default', 1, {
    primary: colors.white,
    swatchPalette: {
      primary: colors.white,
    },
    activeText: lighten(0.3, colors.black),
    text: colors.black,
    backgroundPrimary: 'white',
    backgroundSecondary: 'linear-gradient(180deg, #2D2D2D 0%, #151515 100%)',
    progressIndicator: colors.black,
    titleTextShadow: colors.black,
    artRestingShadow: colors.white,
    artFeatureShadow: colors.white,
    artOverlay: colors.white,
    loadingShadow: colors.white,
    journeyTextBackground: colors.white,
    journeyTextUnderline: colors.black,
    journeyEndVariantBackground: '#2D2D2D',
  }),
  createTheme('googieSilverline', 0.28, {
    primary: colors.silverMist,
    swatchPalette: {
      primary: colors.blancDeBlanc,
      secondary: colors.silverMist,
    },
    text: colors.black,
    backgroundPrimary:
      'linear-gradient(135deg, #EAEBEC 0%, #D0D3D4 61%, #C4C8C9 100%)',
    backgroundSecondary:
      'linear-gradient(180deg, #FCFFFF 6%, #E7ECEC 52%, #D1D6D6 99%)',
    activeText: lighten(0.3, colors.black),
    progressIndicator: '#A0A7AA',
    titleTextShadow: '#FFFFFF',
    artRestingShadow: 'rgba(20,26,29,0.15)',
    artFeatureShadow: 'rgba(20,26,29,0.50)',
    artOverlay: 'rgba(255,255,255,0.50)',
    loadingShadow: 'rgba(233,234,232,0.75)',
    journeyTextBackground: colors.silverMist,
    journeyTextUnderline: '#F1F1F1',
    journeyEndVariantBackground: '#FCFFFF',
  }),
  createTheme('indigoHavana', 0.1, {
    primary: colors.chestnut,
    swatchPalette: {
      primary: colors.desertFlower,
      secondary: colors.chestnut,
    },
    text: colors.white,
    activeText: darken(0.2, colors.white),
    backgroundPrimary:
      'linear-gradient(180deg, #AB8875 0%, #8F6E5D 57%, #796053 100%)',
    backgroundSecondary:
      'linear-gradient(180deg, #F59D91 0%, #FF9A90 54%, #DB847B 100%)',
    progressIndicator: '#B68A74',
    titleTextShadow: '#FE9A90;',
    artRestingShadow: 'rgba(37,7,0,0.25)',
    artFeatureShadow: 'rgba(37,7,0,0.60)',
    artOverlay: 'rgba(143,110,93,0.50)',
    loadingShadow: 'rgba(143,110,93,0.75)',
    journeyTextBackground: colors.chestnut,
    journeyTextUnderline: '#43355D',
    journeyEndVariantBackground: '#F59D91',
  }),
  createTheme('mistiPinku', 0.32, {
    primary: colors.mistiPink,
    swatchPalette: {
      primary: colors.carmineRose,
      secondary: colors.mistiPink,
    },
    text: colors.black,
    backgroundPrimary:
      'linear-gradient(180deg, #FBE2F0 6%, #EDD5E2 56%, #D5BCC9 97%)',
    backgroundSecondary:
      'linear-gradient(180deg, #EB71A4 0%, #DD5A91 55%, #B94B79 100%)',
    activeText: lighten(0.3, colors.black),
    progressIndicator: '#F3A3CD',
    titleTextShadow: '#DF5E94',
    artRestingShadow: 'rgba(70,0,36,0.20)',
    artFeatureShadow: 'rgba(70,0,36,0.50)',
    artOverlay: 'rgba(237,213,226,0.60)',
    loadingShadow: 'rgba(221,90,145,0.50)',
    journeyTextBackground: colors.mistiPink,
    journeyTextUnderline: '#FF80B5',
    journeyEndVariantBackground: '#EC71A4',
  }),
  createTheme('bushBerryville', 0.06, {
    primary: colors.boysenberry,
    swatchPalette: {
      primary: colors.grapeJuice,
      secondary: colors.boysenberry,
    },
    text: colors.white,
    backgroundPrimary:
      'linear-gradient(180deg, #A14275 0%, #873260 56%, #6D2B4F 100%)',
    backgroundSecondary:
      'linear-gradient(180deg, #702E5F 3%, #552248 55%, #451E3B 97%)',
    activeText: darken(0.2, colors.white),
    progressIndicator: '#D75CAD',
    titleTextShadow: '#5A244C',
    artRestingShadow: 'rgba(2,0,1,0.30)',
    artFeatureShadow: 'rgba(2,0,1,0.60)',
    artOverlay: 'rgba(130,48,90,0.60)',
    loadingShadow: 'rgba(104,41,97,0.75)',
    journeyTextBackground: colors.boysenberry,
    journeyTextUnderline: '#682961',
    journeyEndVariantBackground: '#702F5F',
  }),
  createTheme('reddRumble', 0.1, {
    primary: colors.moltenLava,
    swatchPalette: {
      primary: colors.moltenLava,
      secondary: colors.hauteRed,
    },
    activeText: darken(0.2, colors.white),
    text: colors.white,
    backgroundPrimary:
      'linear-gradient(180deg, #D43938 2%, #B82928 58%, #9D2423 96%)',
    backgroundSecondary:
      'linear-gradient(180deg, #CB3440 8%, #9C212D 57%, #841D27 97%)',
    progressIndicator: '#FF605E',
    titleTextShadow: '#A82632',
    artRestingShadow: 'rgba(24,0,0,0.25)',
    artFeatureShadow: 'rgba(24,0,0,0.60)',
    artOverlay: 'rgba(184,41,40,0.50)',
    loadingShadow: 'rgba(164,31,50,0.75)',
    journeyTextBackground: colors.moltenLava,
    journeyTextUnderline: '#A7201F',
    journeyEndVariantBackground: '#CC3541',
  }),
  createTheme('peachesCopperwire', 0.16, {
    text: colors.black,
    primary: colors.peachCream,
    swatchPalette: {
      primary: colors.acacia,
      secondary: colors.peachCream,
    },
    backgroundPrimary:
      'linear-gradient(180deg, #FFBAB7 4%, #F5A8A5 49%, #D79491 97%)',
    backgroundSecondary:
      'linear-gradient(180deg, #FFF189 0%, #E3D469 55%, #D4C664 100%)',
    activeText: lighten(0.3, colors.black),
    progressIndicator: '#BF7978',
    titleTextShadow: '#E8D96E',
    artRestingShadow: 'rgba(86,3,0,0.20)',
    artFeatureShadow: 'rgba(86,3,0,0.55)',
    artOverlay: 'rgba(245,168,165,0.60)',
    loadingShadow: 'rgba(245,168,165,0.75)',
    journeyTextBackground: colors.peachCream,
    journeyTextUnderline: '#DDD161',
    journeyEndVariantBackground: '#FFF189',
  }),
  createTheme('goldieSamba', 0.32, {
    primary: colors.blazingYellow,
    swatchPalette: {
      primary: colors.aquaSky,
      secondary: colors.blazingYellow,
    },
    text: colors.black,
    backgroundPrimary:
      'linear-gradient(180deg, #FFF667 4%, #F7E94C 54%, #F8E72C 93%)',
    backgroundSecondary:
      'linear-gradient(180deg, #A8E2E2 0%, #7FCDCD 52%, #7DBDBD 100%)',
    activeText: lighten(0.3, colors.black),
    progressIndicator: '#D4C73A',
    titleTextShadow: '#89D2D2',
    artRestingShadow: 'rgba(40,31,0,0.25)',
    artFeatureShadow: 'rgba(40,31,0,0.60)',
    artOverlay: 'rgba(248,231,44,0.50)',
    loadingShadow: 'rgba(238,226,99,0.75)',
    journeyTextBackground: '#F7E94C',
    journeyTextUnderline: '#62D167',
    journeyEndVariantBackground: '#A8E2E2',
  }),
  createTheme('fetischeNoir', 0.04, {
    primary: colors.superBlack,
    swatchPalette: {
      primary: '#262020',
      secondary: '#100E0F',
    },
    text: colors.white,
    activeText: darken(0.2, colors.white),
    backgroundPrimary:
      'linear-gradient(180deg, #2E2C2C 2%, #262525 55%, #232020 100%)',
    backgroundSecondary:
      'linear-gradient(180deg, #393838 0%, #323232 54.51%, #282626 100%)',
    progressIndicator: '#676565',
    titleTextShadow: '#000000',
    artRestingShadow: 'rgba(0,0,0,0.35)',
    artFeatureShadow: 'rgba(0,0,0,0.70)',
    artOverlay: 'rgba(0,0,0,0.60)',
    loadingShadow: 'rgba(16,15,15,0.75)',
    journeyTextBackground: '#262525',
    journeyTextUnderline: '#100F0F',
    journeyEndVariantBackground: '#3A3939',
  }),
]

export const defaultTheme =
  themes.find((t) => t.name === 'default') || themes[0]

export default themes
